import type { InputBarRoutingIntent } from "@/stores/input-bar-target-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { getMyWindowLabel } from "@/lib/owner-state-client";
import {
  isPromptReceiptPending,
  waitForPromptReceipt,
  type PromptRetryStatus,
} from "@/lib/prompt-receipt-bus";
import { releaseLocalConversationFrameCorrelation } from "@/lib/local-conversation-frame-correlation";
import { clearPromptRetryEnvelopeForRequest } from "@/lib/prompt-retry-envelope";

export interface PromptRoutingClaim {
  leaseRequired: boolean;
  ownerId: string;
  owned: boolean;
}

export function claimPromptRoutingIntent({
  routingIntent,
  knownRequestId,
  retryStatus,
}: {
  routingIntent: InputBarRoutingIntent | null;
  knownRequestId?: string;
  retryStatus?: PromptRetryStatus;
}): PromptRoutingClaim {
  const leaseRequired = !!routingIntent?.startNewConversation;
  const ownerId = `${getMyWindowLabel() || "web"}:${crypto.randomUUID()}`;
  const alreadyPending =
    !!knownRequestId && isPromptReceiptPending(knownRequestId);
  const owned =
    !leaseRequired ||
    (!alreadyPending &&
      !!routingIntent &&
      useInputBarTarget
        .getState()
        .claimNewConversationIntent(
          routingIntent,
          ownerId,
          Date.now(),
          retryStatus === "timed_out" || retryStatus === "late_accepted",
        ));
  return { leaseRequired, ownerId, owned };
}

export function registerPromptRoutingClaim(
  claim: PromptRoutingClaim,
  requestId: string,
  routingIntent: InputBarRoutingIntent | null,
): void {
  if (!claim.leaseRequired || !routingIntent) return;
  useInputBarTarget
    .getState()
    .registerPromptRoutingRequest(requestId, routingIntent, claim.ownerId);
}

export function waitForDispatchedPromptReceipt({
  clientRequestId,
  retryKey,
  composerId,
  routingIntent,
  claim,
  registeredLocalCorrelation,
}: {
  clientRequestId: string;
  retryKey: string;
  composerId: string;
  routingIntent: InputBarRoutingIntent | null;
  claim: PromptRoutingClaim;
  registeredLocalCorrelation: boolean;
}): Promise<boolean> {
  const releaseLease = () => {
    if (claim.leaseRequired && routingIntent) {
      useInputBarTarget
        .getState()
        .releaseNewConversationClaim(routingIntent, claim.ownerId);
    }
  };
  return waitForPromptReceipt({
    clientRequestId,
    retryKey,
    composerId,
    routingIntent,
    onDefinitiveRejection: () => {
      if (registeredLocalCorrelation) {
        releaseLocalConversationFrameCorrelation(clientRequestId);
      }
      releaseLease();
    },
    onTimeout: releaseLease,
    onAccepted: () => {
      clearPromptRetryEnvelopeForRequest(clientRequestId);
      releaseLease();
    },
  });
}
